import React, { FC, memo } from 'react'

import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { Colors } from 'common-constants/colors'
import { useDraftMessage } from 'components/page/ContactList/hooks/useDraftMessage'
import {
  MessageContainer,
  IconWrapper,
} from 'components/page/ContactList/LastMessage/LastMessage.styled'
import { LastMessageTextSwitcher } from 'components/page/ContactList/LastMessageTextSwitcher'
import { ArrowMessageSvg } from 'components/presentational/svg/ArrowMessageSvg'
import {
  LastMessage as LastMessageInterface,
  Profile,
} from 'reducers/contactList/contactList.types'
import { SpaceTimeLocation } from 'reducers/profile/ProfileApi5.types'

/**
 * На будущее
 * контакт состоит из нескольких блоков
 * из двух:
 * 1. Имя
 * 2. Текст сообщения (или черновик)
 * из трех
 * 1. Бейджы(удаление и т.п.)
 * 2. Имя
 * 3. Текст сообщения (или черновик)
 * В зависимости от состава текст ведет себя по-разному
 * Либо в 2 строки(2 блока), в одну строку (3 блока)
 * Пока просто передам флаг, чтобы можно было регулировать
 * isInlineMessage
 * @param lastMessage
 * @param profile
 * @param lastVisit
 * @param spaceTimeLocation - не у всех анкет есть spaceTimeLocation, поэтому надо проверять на само поле и на поле text внутри.
 * @constructor
 */
export const LastMessage: FC<{
  lastMessage: LastMessageInterface
  profile: Profile
  lastVisit: string | undefined
  spaceTimeLocation: SpaceTimeLocation
  isInlineMessage?: boolean
}> = memo(
  ({ lastMessage, profile, lastVisit, spaceTimeLocation, isInlineMessage }) => {
    const { id } = profile

    const draftMessage = useDraftMessage(id)

    if (draftMessage && !profile.deleted) {
      return (
        <MessageContainer>
          <MessageContent>
            <Message $isInlineMessage={isInlineMessage}>
              <DraftMessageTitle>
                <FormattedMessage id="draft.title" defaultMessage="Черновик:" />
              </DraftMessageTitle>
              <span>{draftMessage}</span>
            </Message>
          </MessageContent>
        </MessageContainer>
      )
    }

    if (lastMessage) {
      const strokeColor = lastMessage.unread ? Colors.location : Colors.link

      if (profile.deleted) {
        return (
          <MessageContainer>
            {profile.deleted && (
              <MessageContent>
                <Message $isInlineMessage={isInlineMessage}>
                  <FormattedMessage
                    id="app.account_deleted"
                    defaultMessage="Анкета удалена"
                  />
                </Message>
              </MessageContent>
            )}
          </MessageContainer>
        )
      }

      if (lastMessage.incoming) {
        return (
          <MessageContainer>
            <Message>
              {profile.isBot && (
                <span
                  data-name="bot"
                  dangerouslySetInnerHTML={{
                    __html: lastMessage.message_text,
                  }}
                />
              )}
              {!profile.isBot && (
                <LastMessageTextSwitcher
                  lastMessage={lastMessage}
                  gender={profile.gender}
                />
              )}
            </Message>
          </MessageContainer>
        )
      } else {
        return (
          <MessageContainer>
            <MessageContent>
              <Message $isInlineMessage={isInlineMessage}>
                <IconWrapper>
                  <ArrowMessageSvg strokeColor={strokeColor} size={16} />
                </IconWrapper>
                <LastMessageTextSwitcher
                  lastMessage={lastMessage}
                  gender={profile.gender}
                />
              </Message>
            </MessageContent>
          </MessageContainer>
        )
      }
    }

    if (lastVisit) {
      return <MessageContainer>{lastVisit}</MessageContainer>
    }

    if (spaceTimeLocation && spaceTimeLocation.text) {
      return <MessageContainer>{spaceTimeLocation.text}</MessageContainer>
    }

    return null
  }
)

export const Message = styled.div<{ $isInlineMessage?: boolean }>`
  display: block;
  display: -webkit-box;
  -webkit-line-clamp: ${(props) => (props.$isInlineMessage ? 1 : 2)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-wrap: break-word;
  text-overflow: ellipsis;
  min-width: 0;
`

Message.defaultProps = {
  $isInlineMessage: false,
}

const MessageContent = styled.div`
  display: flex;
`

const DraftMessageTitle = styled.span`
  color: ${Colors.warning};
  margin-bottom: 5px;
  ${(props) => props.theme.marginRight}: 5px;
`
